import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import { isWithinInterval } from 'date-fns';
import get from 'lodash.get';
import ConfirmationPopup from './ConfirmationPopup';
import { KEY_CODES } from '../../utils/shared/accessibilityUtils';
import {
    CLASS_NAME,
    CALENDAR_MENU,
    DATE_RANGE_ALERT_TYPE,
    DATE_RANGE_STRICTLY_ENFORCED_ALERT_TYPE,
} from '../../constants/shared/WebEntryConstants';
import { EMPTY, TITLE } from '../../constants/shared/CommonConstants';
import { hasAllData, isAllDataEmpty } from './validation';
import useClickOutside from '../../hooks/OutsideClicker';
import { getFieldByType, getDate, isEntering } from './utils';

const TableRow = ({
    isFirstStudentInList,
    isEditable,
    result,
    studentInfo,
    grade,
    isPmPanelType,
    panelType,
    invalidateResult,
    setSelectedGradeDate,
    selectedGradeDate,
    windowDates,
    isChosenSchoolWindowDatesStrictlyEnforced,
    formIdsData,
    error,
    processValidationAndSaving,
    rowStatus,
    getCellsData,
    webEntryClassName,
    assessmentType,
    isSecurePeriod,
}) => {
    const adjustedResult = isEditable
        ? result
        : {
              date: null,
              itemLevelDetails: {},
          };
    const {
        date: adjustedDate,
        formGrade: adjustedformGrade,
        itemLevelDetails: {
            correct_responses: adjustedCorrectResponses,
            incorrect_responses: adjustedIncorrectResponses,
        },
    } = adjustedResult;

    const datePickerRef = useRef();
    const invalidateButtonRef = useRef();
    const fieldsLineElementRef = useRef();

    const [alertType, setAlertType] = useState(null);
    const [rowData, setRowData] = useState({
        correctResponses: adjustedCorrectResponses,
        incorrectResponses: adjustedIncorrectResponses,
        date: getDate(adjustedDate, selectedGradeDate.selectedDate),
        formGrade: adjustedformGrade,
        formValues: {},
    });

    const isRowDisabled = panelType === TITLE.BENCHMARK_TITLE ? isSecurePeriod : false;
    const [rowConfig, setRowConfig] = useState({
        isRowDisabled,
        isRowSaving: false,
        isRowFocused: false,
        isEnteringStarted: false,
        isAlreadyCreated: get(
            rowStatus,
            'isAlreadyCreated',
            hasAllData([adjustedDate, adjustedCorrectResponses, adjustedIncorrectResponses]),
        ),
    });

    const { selectedDate, studentId, isDateForAllStudents } = selectedGradeDate;
    const { correctResponses, incorrectResponses, formValues } = rowData;
    const isRowEmpty = isAllDataEmpty([
        rowData.date,
        rowData.correctResponses,
        rowData.incorrectResponses,
    ]);

    const invalidateHandler = () => {
        setRowConfig(prevState => ({
            ...prevState,
            isAlreadyCreated: false,
        }));
        invalidateResult(studentInfo.id, result?.resultId);
    };

    const setInitialFormValues = () => {
        const conditionForPmPanels = isEditable && isPmPanelType && rowConfig.isAlreadyCreated;
        if (!isEmpty(formIdsData) && (!isPmPanelType || conditionForPmPanels)) {
            setRowData(prevData => ({
                ...prevData,
                formValues:
                    formIdsData.find(formIdObject =>
                        conditionForPmPanels
                            ? formIdObject.formId === result.formUid
                            : formIdObject.grade === grade,
                    ) || {},
            }));
        }
    };

    const setIsRowFocused = newValue => {
        setRowConfig(prevState => ({
            ...prevState,
            isRowFocused: newValue,
        }));
    };

    const handleDateValueChange = (dateValue, isDateForAllStudentsEnabled) => {
        setSelectedGradeDate(prev => ({
            ...prev,
            [grade]: {
                selectedDate: dateValue,
                studentId: studentInfo.id,
                isDateForAllStudents: isDateForAllStudentsEnabled,
            },
        }));
        const isDateInRange = isWithinInterval(new Date(dateValue), {
            start: new Date(windowDates.startDate),
            end: new Date(windowDates.endDate),
        });
        if (panelType === TITLE.BENCHMARK_TITLE && !isDateInRange)
            setAlertType(
                isChosenSchoolWindowDatesStrictlyEnforced
                    ? DATE_RANGE_STRICTLY_ENFORCED_ALERT_TYPE
                    : DATE_RANGE_ALERT_TYPE,
            );

        setRowData(prevState => ({
            ...prevState,
            date: dateValue,
        }));
    };

    const handleNumberInputChange = (e, stateFieldName) => {
        setRowData(prevState => ({
            ...prevState,
            [stateFieldName]: isEmpty(e.target.value) ? null : Number(e.target.value),
        }));
    };

    const setFormGradeHandler = formGradeValue => {
        setRowData(prevState => ({
            ...prevState,
            formGrade: formGradeValue,
            formValues: prevState.formValues || {},
        }));
    };

    const setFormValuesHandler = chosenFormId => {
        setRowData(prevState => ({
            ...prevState,
            formValues: formIdsData.find(item => item.formId === chosenFormId),
        }));
    };

    const handleKeyDown = e => {
        const isTABKeyCode = e.keyCode === KEY_CODES.tab;
        const isTabOnLastInput =
            !e.shiftKey && isTABKeyCode && e.target.classList.contains('incorrect-responses');
        const isTargetDatePicker = Array.from(e.target.children).find(
            child => child === datePickerRef.current,
        );
        const isShiftTabOnDatePicker = e.shiftKey && isTABKeyCode && isTargetDatePicker;

        if (
            (isTabOnLastInput || isShiftTabOnDatePicker) &&
            rowConfig.isEnteringStarted &&
            !rowConfig.isRowDisabled
        ) {
            processValidationAndSaving(rowData, setIsRowFocused);
        }
    };

    const handleClickOutside = () => {
        if (alertType) return;
        const fieldValues = [correctResponses, incorrectResponses, error];
        if (isPmPanelType) {
            fieldValues.push(rowData.formGrade, formValues.sequence);
        }
        const triggerValidation = fieldValues.some(item => !isNil(item) || !isEmpty(item));

        if (
            triggerValidation &&
            rowConfig.isRowFocused &&
            !rowConfig.isRowDisabled &&
            !rowConfig.isAlreadyCreated
        ) {
            processValidationAndSaving(rowData, setIsRowFocused);
        }
    };

    const handleLineClick = e => {
        if (alertType) return;
        const calendar = fieldsLineElementRef.current.querySelector(`.${CALENDAR_MENU}`);
        const isCalendar = calendar && calendar.contains(e.target);
        const isRowFocusedFlag =
            e.target.classList.contains(CLASS_NAME.PREVENT_VALIDATION) || isCalendar;
        setIsRowFocused(isRowFocusedFlag || alertType);

        if (
            !isRowFocusedFlag &&
            rowConfig.isEnteringStarted &&
            !rowConfig.isRowDisabled &&
            !rowConfig.isAlreadyCreated &&
            !rowConfig.isRowSaving
        ) {
            processValidationAndSaving(rowData, setIsRowFocused);
        }
    };

    useClickOutside(fieldsLineElementRef, handleClickOutside);

    useEffect(() => {
        setRowConfig(prevState => ({
            ...prevState,
            isRowDisabled: rowStatus?.isRowDisabled || isRowDisabled,
            isRowSaving: rowStatus?.isRowSaving,
            isAlreadyCreated: get(rowStatus, 'isAlreadyCreated', rowConfig.isAlreadyCreated),
        }));
    }, [rowStatus]);

    useEffect(() => {
        setInitialFormValues();
    }, []);

    useEffect(() => {
        setInitialFormValues();
    }, [formIdsData]);

    useEffect(() => {
        if (
            studentInfo.id === studentId ||
            (isDateForAllStudents &&
                (isNil(rowData.correctResponses) || isNil(rowData.incorrectResponses)))
        ) {
            setRowData(prevState => ({
                ...prevState,
                date: getDate(rowData.date, selectedDate),
            }));
        }
    }, [selectedGradeDate.selectedDate]);

    useEffect(() => {
        setRowConfig(prevData => ({
            ...prevData,
            isEnteringStarted: isEntering(error, isPmPanelType, rowData),
        }));
    }, [correctResponses, incorrectResponses, formValues]);

    const handlers = {
        handleDateValueChange,
        handleKeyDown,
        handleNumberInputChange,
        setFormGradeHandler,
        setFormValuesHandler,
    };

    const elementsRef = {
        invalidateButtonRef,
        datePickerRef,
    };

    const cellsData = getCellsData({
        grade,
        studentInfo,
        result,
        error,
        handlers,
        elementsRef,
        rowData,
        rowConfig,
        setIsRowFocused,
        setAlertType,
    });

    return (
        <div
            className={`${webEntryClassName}-row-container ${
                isFirstStudentInList && 'first-in-grade'
            }`}
        >
            <ConfirmationPopup
                alertType={alertType}
                assessmentType={assessmentType}
                invalidateResult={invalidateHandler}
                setAlertType={setAlertType}
                datePickerRef={datePickerRef}
                invalidateButtonRef={invalidateButtonRef}
                updateRowData={updatedObject =>
                    setRowData(prevState => ({
                        ...prevState,
                        ...updatedObject,
                    }))
                }
                isPmPanelType={isPmPanelType}
            />
            <div
                className={`table-fields-line ${isRowEmpty && !error ? EMPTY : ''}`}
                role="button"
                tabIndex={-1}
                onClick={handleLineClick}
                ref={fieldsLineElementRef}
            >
                {cellsData.map((cellData, index) => {
                    return getFieldByType(cellData, index, studentInfo, isSecurePeriod);
                })}
            </div>
        </div>
    );
};

TableRow.defaultProps = {
    grade: '',
    panelType: '',
    assessmentType: '',
    isFirstStudentInList: false,
    isEditable: false,
    isPmPanelType: false,
    isChosenSchoolWindowDatesStrictlyEnforced: false,
    selectedGradeDate: null,
    error: null,
    rowStatus: null,
    formIdsData: [],
    studentInfo: {},
    windowDates: {},
    result: {
        date: null,
        itemLevelDetails: {},
    },
    invalidateResult: () => {},
    setSelectedGradeDate: () => {},
    processValidationAndSaving: () => {},
    getCellsData: () => {},
    isSecurePeriod: false,
};

TableRow.propTypes = {
    webEntryClassName: PropTypes.string.isRequired,
    grade: PropTypes.string,
    panelType: PropTypes.string,
    assessmentType: PropTypes.string,
    isFirstStudentInList: PropTypes.bool,
    isEditable: PropTypes.bool,
    isPmPanelType: PropTypes.bool,
    isChosenSchoolWindowDatesStrictlyEnforced: PropTypes.bool,
    selectedGradeDate: PropTypes.object,
    error: PropTypes.object,
    rowStatus: PropTypes.object,
    formIdsData: PropTypes.array,
    studentInfo: PropTypes.object,
    windowDates: PropTypes.object,
    result: PropTypes.object,
    invalidateResult: PropTypes.func,
    setSelectedGradeDate: PropTypes.func,
    processValidationAndSaving: PropTypes.func,
    getCellsData: PropTypes.func,
    isSecurePeriod: PropTypes.bool,
};

export default TableRow;
